// src/utils/intelligence.js
// Cross-domain intelligence layer.
// Computes connections between domains and surfaces them as insights,
// system prompt context, and proactive nudges.

import { TODAY, df } from './dates';

// ─────────────────────────────────────────────────────────────────────────────
// Cross-domain connection engine
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Computes cross-domain connections from app state.
 * Returns an array of insight objects with { type, priority, message, action?, domain }.
 */
export function computeCrossDomainInsights(st) {
  const insights = [];
  const now = new Date();
  const hour = now.getHours();

  const tasks    = st.tasks    || [];
  const events   = st.events   || [];
  const workouts = st.workouts || [];
  const vinyl    = st.vinyl    || [];
  const goals    = st.goals    || [];
  const finance  = st.finance  || [];
  const income   = st.income   || [];
  const routines = st.routines || [];

  // ── Shift → workout suppression ──────────────────────────────────────────
  const todayShift = events.find(e =>
    e.date === TODAY && /shift|work|ed|er|icu|floor/i.test(e.title || e.type || '')
  );
  const workoutToday = workouts.find(w => w.date === TODAY);
  const routinesDueToday = routines.filter(r => r.lastDone !== TODAY);
  const hasWorkoutRoutine = routinesDueToday.some(r =>
    /run|workout|gym|exercise|lift|train|walk|bike|swim/i.test(r.name || '')
  );

  if (todayShift && !workoutToday && hasWorkoutRoutine) {
    insights.push({
      type: 'shift_workout',
      priority: 2,
      domain: 'health',
      message: `You have a shift today (${todayShift.title}). Workout routine pending — consider logging rest day.`,
      action: 'log_workout',
    });
  }

  // ── Payday → finance tasks ────────────────────────────────────────────────
  const nextPayday = income
    .filter(i => i.nextDate)
    .sort((a, b) => new Date(a.nextDate) - new Date(b.nextDate))[0];
  const paydayDays = nextPayday ? df(nextPayday.nextDate) : null;

  if (paydayDays !== null && paydayDays <= 2 && paydayDays >= 0) {
    const financeTasks = tasks.filter(t =>
      !t.done && /bill|pay|transfer|budget|rent|mortgage|insurance/i.test(t.text || '')
    );
    if (financeTasks.length > 0) {
      insights.push({
        type: 'payday_tasks',
        priority: 1,
        urgent: false,
        domain: 'finance',
        message: `Payday ${paydayDays === 0 ? 'is today' : 'is tomorrow'} — you have ${financeTasks.length} finance-related task${financeTasks.length > 1 ? 's' : ''} pending.`,
        tasks: financeTasks.slice(0, 2),
      });
    }
  }

  // ── Vinyl ETA → dashboard ─────────────────────────────────────────────────
  const incomingVinyl = vinyl
    .filter(v => !v.delivered && v.status === 'shipped' && v.eta)
    .sort((a, b) => df(a.eta) - df(b.eta));

  if (incomingVinyl.length > 0) {
    const next = incomingVinyl[0];
    const d = df(next.eta);
    if (d >= 0 && d <= 3) {
      insights.push({
        type: 'vinyl_arriving',
        priority: 3,
        domain: 'vinyl',
        message: `${next.artist} — ${next.title || 'incoming'} arrives ${d === 0 ? 'today' : d === 1 ? 'tomorrow' : `in ${d} days`}.`,
      });
    }
  }

  // ── Race goal → training nudge ────────────────────────────────────────────
  const raceGoal = goals.find(g =>
    /marathon|race|5k|10k|half|triathlon/i.test(g.title || '') &&
    g.targetDate && df(g.targetDate) >= 0
  );
  const recentWorkouts = workouts.filter(w => df(w.date) >= -6 && df(w.date) <= 0).length;

  if (raceGoal) {
    const daysLeft = df(raceGoal.targetDate);
    if (daysLeft <= 90 && recentWorkouts < 3) {
      insights.push({
        type: 'race_training',
        priority: 1,
        domain: 'health',
        message: `${raceGoal.title} is ${daysLeft}d away — only ${recentWorkouts} workout${recentWorkouts !== 1 ? 's' : ''} logged this week.`,
        action: 'log_workout',
      });
    }
  }

  // ── Overdue tasks + today events ─────────────────────────────────────────
  const overdueTasks = tasks.filter(t =>
    !t.done && t.dueDate && df(t.dueDate) < 0
  );
  const todayEvents = events.filter(e => e.date === TODAY);

  if (overdueTasks.length > 0 && todayEvents.length > 0) {
    insights.push({
      type: 'overdue_busy_day',
      priority: 2,
      domain: 'tasks',
      message: `${overdueTasks.length} overdue task${overdueTasks.length > 1 ? 's' : ''} with ${todayEvents.length} event${todayEvents.length > 1 ? 's' : ''} today — tight day.`,
      action: 'view_tasks',
    });
  }

  // ── Bill due — all bills due within 3 days, priority ordered ────────────────
  const seenFinanceIds = new Set();

  const upcomingBills = finance.filter(f =>
    f.next && df(f.next) >= 0 && df(f.next) <= 3
  ).sort((a, b) => df(a.next) - df(b.next));

  for (const bill of upcomingBills) {
    if (!seenFinanceIds.has(bill.id)) {
      seenFinanceIds.add(bill.id);
      const daysUntil = df(bill.next);
      insights.push({
        id: `bill-${bill.id}`,   // Finding 4: stable id for dedup
        type: 'bill_due',
        priority: daysUntil === 0 ? 1 : 2,
        urgent: daysUntil === 0,
        domain: 'finance',
        message: `${bill.name} ${daysUntil === 0 ? 'due today' : daysUntil === 1 ? 'due tomorrow' : `due in ${daysUntil}d`}${bill.amount ? ` — $${bill.amount}` : ''}.`,
        action: 'view_finance',
      });
    }
  }

  // ── Routine streak at risk ────────────────────────────────────────────────
  const atRisk = routines.filter(r => {
    if (r.lastDone === TODAY) return false;
    const history = r.history || [];
    const yesterday = new Date(TODAY);
    yesterday.setDate(yesterday.getDate() - 1);
    const yStr = yesterday.toISOString().slice(0, 10);
    const hasStreak = history.includes(yStr);
    const streakLen = history.filter(d => {
      const diff = df(d);
      return diff >= -6 && diff < 0;
    }).length;
    return hasStreak && streakLen >= 3;
  });

  if (atRisk.length > 0 && hour >= 18) {
    insights.push({
      type: 'streak_risk',
      priority: 2,
      domain: 'routines',
      message: `${atRisk[0].name} streak at risk — not checked in today.`,
      action: 'view_routines',
    });
  }

  return insights.sort((a, b) => a.priority - b.priority);
}

// ─────────────────────────────────────────────────────────────────────────────
// System prompt enrichment
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds a rich cross-domain context string for injection into the AI system prompt.
 * Surfaces connections the AI can reference when answering questions.
 */
export function buildIntelligenceContext(st) {
  const insights = computeCrossDomainInsights(st);
  if (insights.length === 0) return '';

  const lines = insights.map(i => `- ${i.message}`).join('\n');
  return `\n\nCROSS-DOMAIN CONTEXT (proactively reference these when relevant):\n${lines}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Morning brief data assembly
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Assembles all data needed for the morning brief view.
 * Returns a structured object the MorningBriefPage can render directly.
 */
export function buildMorningBriefData(st, sigs = []) {
  const tasks    = st.tasks    || [];
  const events   = st.events   || [];
  const workouts = st.workouts || [];
  const vinyl    = st.vinyl    || [];
  const goals    = st.goals    || [];
  const finance  = st.finance  || [];
  const income   = st.income   || [];
  const routines = st.routines || [];

  const todayTasks = tasks.filter(t =>
    !t.done && (t.bucket === 'today' || t.dueDate === TODAY)
  );
  const overdueTasks = tasks.filter(t =>
    !t.done && t.dueDate && df(t.dueDate) < 0
  );
  const todayEvents = events
    .filter(e => e.date === TODAY)
    .sort((a, b) => (a.time || '').localeCompare(b.time || ''));

  const todayRoutines = routines.filter(r => r.lastDone !== TODAY);

  const remindersToday = tasks.filter(t => {
    if (!t.reminderAt) return false;
    const at = new Date(t.reminderAt);
    const atStr = at.toISOString().slice(0, 10);
    return atStr === TODAY;
  }).sort((a, b) => new Date(a.reminderAt) - new Date(b.reminderAt));

  // Finance snapshot
  const nextPayday = income
    .filter(i => i.nextDate)
    .sort((a, b) => new Date(a.nextDate) - new Date(b.nextDate))[0];
  const paydayDays = nextPayday ? df(nextPayday.nextDate) : null;
  const urgentBill = finance
    .filter(f => f.next && df(f.next) >= 0 && df(f.next) <= 3)
    .sort((a, b) => df(a.next) - df(b.next))[0];

  // Health snapshot
  const raceGoal = goals.find(g =>
    /marathon|race|5k|10k|half|triathlon/i.test(g.title || '') &&
    g.targetDate && df(g.targetDate) >= 0
  );
  const workoutsThisWeek = workouts.filter(w =>
    df(w.date) >= -6 && df(w.date) <= 0
  ).length;

  // Vinyl
  const incomingVinyl = vinyl
    .filter(v => !v.delivered && v.status === 'shipped' && v.eta && df(v.eta) >= 0 && df(v.eta) <= 7)
    .sort((a, b) => df(a.eta) - df(b.eta))
    .slice(0, 2);

  // Alerts
  const alerts = sigs.filter(s => s.lvl === 'warn').slice(0, 2);

  // Cross-domain insights
  const crossInsights = computeCrossDomainInsights(st).slice(0, 3);

  return {
    todayTasks,
    overdueTasks,
    todayEvents,
    todayRoutines,
    remindersToday,
    paydayDays,
    nextPayday,
    urgentBill,
    raceGoal,
    workoutsThisWeek,
    incomingVinyl,
    alerts,
    crossInsights,
    name: st.profile?.name ? st.profile.name.split(' ')[0] : '',
  };
}
